import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { reportApi } from '../api/endpoints.js';
import { queryKeys } from '../app/query-client.js';
import { useSession } from '../auth/session.js';
import { useBoards } from '../hooks/use-directory.js';
import { PageHeader } from '../components/page-header.js';
import { Spinner } from '../components/ui.js';

const RANGES = [7, 14, 30, 90] as const;

function delta(current: number, previous: number): string {
  const diff = current - previous;
  const sign = diff > 0 ? '+' : '';
  return `${sign}${String(diff)} vs previous period (${String(previous)})`;
}

function Metric({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}): ReactNode {
  return (
    <div className="surface-card p-5 dark:border-dark-line dark:bg-dark-panel">
      <p className="eyebrow text-muted dark:text-white">{label}</p>
      <p className="mt-2 font-display text-display text-navy dark:text-dark-ink">{value}</p>
      {hint === undefined ? null : (
        <p className="mt-1 text-[12.5px] text-muted dark:text-white">{hint}</p>
      )}
    </div>
  );
}

function Bar({
  label,
  value,
  max,
}: {
  readonly label: string;
  readonly value: number;
  readonly max: number;
}): ReactNode {
  const width = max === 0 ? 0 : Math.round((value / max) * 100);

  return (
    <div className="mb-2.5">
      <div className="mb-1 flex items-center justify-between text-[12.5px]">
        <span className="font-semibold text-navy dark:text-dark-ink">{label}</span>
        <span className="text-muted">{value}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-navy/6 dark:bg-white/10">
        <div className="h-full rounded-full bg-royal/70" style={{ width: `${String(width)}%` }} />
      </div>
    </div>
  );
}

export function DashboardPage(): ReactNode {
  const { user } = useSession();
  const { boards } = useBoards();
  const [days, setDays] = useState<number>(30);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.reports.dashboard(days),
    queryFn: () => reportApi.dashboard(days),
  });

  return (
    <>
      <PageHeader title={`Good to see you, ${user?.firstName ?? ''}`}>
        <span className="flex rounded-field border border-hairline bg-white p-0.5 dark:border-dark-line dark:bg-dark-field">
          {RANGES.map((range) => (
            <button
              key={range}
              type="button"
              onClick={() => {
                setDays(range);
              }}
              className={`rounded-[9px] px-2.5 py-1 text-[12.5px] font-bold transition sm:px-3 ${
                days === range
                  ? 'bg-navy text-white'
                  : 'text-muted hover:text-navy dark:hover:text-dark-ink'
              }`}
            >
              {range}d
            </button>
          ))}
        </span>
      </PageHeader>

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
        {isLoading ? (
          <div className="grid place-items-center py-20 text-muted">
            <Spinner className="size-6" />
          </div>
        ) : isError || data === undefined ? (
          <p className="py-16 text-center text-[14px] text-muted">
            The dashboard could not be loaded.
          </p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Metric label="Open" value={String(data.headline.open)} />
              <Metric label="On hold" value={String(data.headline.onHold)} />
              <Metric
                label="Unassigned"
                value={String(data.headline.unassigned)}
                hint="Nobody has picked these up"
              />
              <Metric
                label="Quiet for a week"
                value={String(data.headline.staleCount)}
                hint="Open and untouched"
              />
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Metric
                label={`Closed in ${String(days)}d`}
                value={String(data.headline.closedInRange)}
                hint={delta(data.headline.closedInRange, data.headline.closedPreviousRange)}
              />
              <Metric
                label={`Created in ${String(days)}d`}
                value={String(data.headline.createdInRange)}
                hint={delta(data.headline.createdInRange, data.headline.createdPreviousRange)}
              />
              <Metric
                label="Avg days to close"
                value={
                  data.headline.averageDaysToClose === null
                    ? '–'
                    : String(data.headline.averageDaysToClose)
                }
                hint={
                  data.headline.medianDaysToClose === null
                    ? 'No closed tickets in range'
                    : `Median ${String(data.headline.medianDaysToClose)}`
                }
              />
              <Metric
                label="Avg age of open"
                value={
                  data.headline.averageOpenAgeDays === null
                    ? '–'
                    : `${String(data.headline.averageOpenAgeDays)}d`
                }
              />
            </div>

            <section className="surface-card mt-5 p-5 dark:border-dark-line dark:bg-dark-panel">
              <h2 className="mb-4 text-[15px] font-bold text-navy dark:text-dark-ink">
                Created vs closed per day
              </h2>
              <div className="flex h-28 items-end gap-0.5">
                {data.trend.map((point) => {
                  const peak = Math.max(...data.trend.map((p) => Math.max(p.created, p.closed)), 1);
                  return (
                    <div
                      key={point.day}
                      className="flex h-full min-w-0 flex-1 items-end gap-px"
                      title={`${point.day}: ${String(point.created)} created, ${String(point.closed)} closed`}
                    >
                      <div
                        className="w-1/2 rounded-t-sm bg-navy/25 dark:bg-white/25"
                        style={{ height: `${String((point.created / peak) * 100)}%` }}
                      />
                      <div
                        className="w-1/2 rounded-t-sm bg-royal/80"
                        style={{ height: `${String((point.closed / peak) * 100)}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="mt-2 text-[12px] text-muted">Grey: created · Blue: closed</p>
            </section>

            <section className="surface-card mt-5 p-5 dark:border-dark-line dark:bg-dark-panel">
              <h2 className="mb-4 text-[15px] font-bold text-navy dark:text-dark-ink">
                Closed per person
              </h2>
              {data.byAssignee.length === 0 ? (
                <p className="text-[13.5px] text-muted">Nothing to show yet.</p>
              ) : (
                data.byAssignee.map((entry) => (
                  <Bar
                    key={entry.userId}
                    label={entry.name}
                    value={entry.closedInRange}
                    max={Math.max(...data.byAssignee.map((item) => item.closedInRange), 1)}
                  />
                ))
              )}
            </section>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <section className="surface-card p-5 dark:border-dark-line dark:bg-dark-panel">
                <h2 className="mb-4 text-[15px] font-bold text-navy dark:text-dark-ink">
                  Load by department
                </h2>
                {data.byDepartment.length === 0 ? (
                  <p className="text-[13.5px] text-muted">Nothing to show yet.</p>
                ) : (
                  data.byDepartment.map((entry) => (
                    <Bar
                      key={entry.departmentId}
                      label={entry.name}
                      value={entry.open + entry.onHold}
                      max={Math.max(
                        ...data.byDepartment.map((item) => item.open + item.onHold),
                        1,
                      )}
                    />
                  ))
                )}
              </section>

              <section className="surface-card p-5 dark:border-dark-line dark:bg-dark-panel">
                <h2 className="mb-4 text-[15px] font-bold text-navy dark:text-dark-ink">
                  Open work per board
                </h2>
                {data.byBoard.length === 0 ? (
                  <p className="text-[13.5px] text-muted">Nothing to show yet.</p>
                ) : (
                  data.byBoard.map((entry) => (
                    <Bar
                      key={entry.boardId}
                      label={entry.name}
                      value={entry.open}
                      max={Math.max(...data.byBoard.map((item) => item.open), 1)}
                    />
                  ))
                )}
              </section>
            </div>

            <section className="surface-card mt-5 p-5 dark:border-dark-line dark:bg-dark-panel">
              <h2 className="mb-4 text-[15px] font-bold text-navy dark:text-dark-ink">
                Waiting the longest
              </h2>

              {data.attention.length === 0 ? (
                <p className="text-[13.5px] text-muted">Nothing is going stale. Good.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {data.attention.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex items-center gap-3 rounded-panel bg-mist/60 px-3 py-2.5 dark:bg-white/5"
                    >
                      <span className="text-[11.5px] font-bold tracking-[0.06em] text-muted">
                        SBG#{entry.number}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-navy dark:text-dark-ink">
                        {entry.title}
                      </span>
                      <span className="text-[12.5px] font-semibold text-caution">
                        {entry.daysQuiet}d quiet
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-5 flex flex-wrap gap-3">
              {boards.map((entry) => (
                <Link
                  key={entry.board.id}
                  to={`/boards/${entry.board.slug}`}
                  className="surface-card px-5 py-4 transition hover:-translate-y-px dark:border-dark-line dark:bg-dark-panel"
                >
                  <span className="block text-[14px] font-bold text-navy dark:text-dark-ink">
                    {entry.board.name}
                  </span>
                  <span className="text-[12.5px] text-muted">Open the board</span>
                </Link>
              ))}
            </section>
          </>
        )}
      </div>
    </>
  );
}
